/**
 * dialogs.js
 *
 * The app's ONE themed dialog: alert, confirm, choice and prompt. Drawn in
 * the theme TOKENS (panel surface, ink, hairlines), centred, with a dim
 * behind, so they follow Light / Dark / Sepia / the band themes like every
 * panel. Vanilla DOM so they work anywhere; one stylesheet (`pdlg-`) is
 * injected once, and components/ConfirmDialog.jsx renders the same classes.
 *
 * Every dialog takes an optional `title` (opts.title). Without one, a message
 * whose first paragraph is short (two blank-line-separated parts) uses that
 * paragraph as the title.
 *
 * Buttons: Cancel quiet, the action primary (accent text on the panel, never
 * a black slab), a destructive action in the danger colour. A destructive
 * confirm starts on Cancel and never fires on Enter.
 */

// THE HOUSE DIALOG IS SMALL (2026-11-05, by the user: "fat"): a 300 to 400px
// card, 13.5px title, 12.5px words, 26px buttons, tight gaps. Every confirm,
// choice, alert and prompt in the app draws from this one sheet.
const CSS = `
.pdlg-overlay{position:fixed;inset:0;z-index:999999;display:flex;align-items:center;justify-content:center;background:rgba(16,22,28,0.45);font-family:inherit;}
.pdlg{box-sizing:border-box;background:var(--surface-panel,#fff);color:var(--text-primary,#1a1a1a);border:1px solid var(--border-default,rgba(0,0,0,.12));border-radius:10px;padding:14px 16px 12px;min-width:300px;max-width:min(400px,92vw);box-shadow:0 20px 60px rgba(0,0,0,.28),0 2px 8px rgba(0,0,0,.12);animation:pdlg-in .16s ease-out;font-family:-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,sans-serif;}
@keyframes pdlg-in{from{opacity:0;transform:scale(.97)}to{opacity:1;transform:scale(1)}}
@media (prefers-reduced-motion: reduce){.pdlg{animation:none}}
.pdlg-title{margin:0 0 4px;font-size:13.5px;font-weight:600;line-height:1.35;color:var(--text-primary,#1a1a1a);}
.pdlg-body{font-size:12.5px;line-height:1.5;color:var(--text-secondary,#4a5561);white-space:pre-line;word-break:break-word;}
.pdlg-body:empty{display:none}
.pdlg-foot{display:flex;justify-content:flex-end;gap:6px;margin-top:12px;}
.pdlg-foot.is-stack{flex-direction:column;align-items:stretch;}
.pdlg-btn{height:26px;padding:0 11px;border-radius:5px;font:inherit;font-size:12px;font-weight:600;cursor:pointer;outline:none;white-space:nowrap;transition:background .1s,border-color .1s,filter .1s;}
.pdlg-btn:focus-visible{box-shadow:0 0 0 2px var(--ring,var(--accent,#2f86c4));}
.pdlg-btn--quiet{border:1px solid var(--border-default,rgba(0,0,0,.15));background:transparent;color:var(--text-secondary,#4a5561);}
.pdlg-btn--quiet:hover{background:var(--surface-hover,rgba(0,0,0,.05));color:var(--text-primary,#1a1a1a);}
.pdlg-btn--primary{border:1px solid color-mix(in srgb,var(--accent-text,var(--accent,#2f86c4)) 45%,transparent);background:var(--surface-panel,#fff);color:var(--accent-text,var(--accent,#2f86c4));}
.pdlg-btn--primary:hover{background:var(--surface-hover,rgba(0,0,0,.05));}
.pdlg-btn--danger{border:1px solid var(--danger,#d64545);background:var(--danger,#d64545);color:#fff;}
.pdlg-btn--danger:hover{filter:brightness(1.08);}
.pdlg-input{width:100%;height:28px;margin-top:8px;padding:0 9px;border-radius:6px;border:1px solid var(--border-default,rgba(0,0,0,.15));background:var(--surface-raised,#f6f7f9);color:var(--text-primary,#1a1a1a);font:inherit;font-size:12.5px;outline:none;box-sizing:border-box;}
.pdlg-input:focus{border-color:var(--border-strong,#9aa6b2);}
`;

let cssInjected = false;
function injectCss() {
  if (cssInjected || typeof document === 'undefined') return;
  cssInjected = true;
  const style = document.createElement('style');
  style.setAttribute('data-pidly-dialogs', '');
  style.textContent = CSS;
  document.head.appendChild(style);
}

// "Title\n\nBody" -> { title, body } when the first part is short.
function splitMessage(message, title) {
  const text = String(message ?? '');
  if (title) return { title, body: text };
  const parts = text.split(/\n\s*\n/);
  if (parts.length > 1 && parts[0].length <= 90) return { title: parts[0].trim(), body: parts.slice(1).join('\n\n').trim() };
  return { title: '', body: text };
}

function frame(message, title) {
  injectCss();
  const overlay = document.createElement('div');
  overlay.className = 'pdlg-overlay';
  const dialog = document.createElement('div');
  dialog.className = 'pdlg pidly-dialog-box'; // laptop fit scales it (ProjectWorkspace.css)
  dialog.setAttribute('role', 'dialog');
  dialog.setAttribute('aria-modal', 'true');
  const { title: t, body: b } = splitMessage(message, title);
  if (t) {
    const h = document.createElement('h2');
    h.className = 'pdlg-title';
    h.textContent = t;
    dialog.appendChild(h);
  }
  const body = document.createElement('div');
  body.className = 'pdlg-body';
  body.textContent = b;
  dialog.appendChild(body);
  const footer = document.createElement('div');
  footer.className = 'pdlg-foot';
  overlay.appendChild(dialog);
  return { overlay, dialog, body, footer };
}

// A red hex from older callers means the same as 'danger'.
const isDanger = (c) => c === 'danger' || /^#(e74c3c|c0392b|d64545|e85c4a)$/i.test(String(c || ''));

function button(label, variant) {
  const btn = document.createElement('button');
  btn.type = 'button';
  btn.className = `pdlg-btn pdlg-btn--${variant}`;
  btn.textContent = label;
  return btn;
}

/**
 * Themed alert dialog. Returns a Promise that resolves when OK is clicked.
 */
export function pidlyAlert(message, opts = {}) {
  return new Promise((resolve) => {
    const { overlay, dialog, footer } = frame(message, opts.title);
    const okBtn = button(opts.okLabel || 'OK', 'primary');
    footer.appendChild(okBtn);
    dialog.appendChild(footer);
    document.body.appendChild(overlay);

    const close = () => {
      if (overlay.parentNode) overlay.parentNode.removeChild(overlay);
      document.removeEventListener('keydown', onKey, true);
      resolve();
    };
    okBtn.onclick = close;
    overlay.onmousedown = (e) => { if (e.target === overlay) close(); };
    const onKey = (e) => {
      if (e.key === 'Escape' || e.key === 'Enter') { e.preventDefault(); e.stopPropagation(); close(); }
    };
    document.addEventListener('keydown', onKey, true);
    requestAnimationFrame(() => okBtn.focus());
  });
}

/**
 * Themed confirm dialog. Returns a Promise<boolean>.
 * @param {string} message
 * @param {{ title?: string, confirmLabel?: string, confirmColor?: string, cancelLabel?: string }} opts
 */
export function pidlyConfirm(message, opts = {}) {
  const { confirmLabel = 'OK', confirmColor, cancelLabel = 'Cancel', title } = opts;
  const danger = isDanger(confirmColor);
  return new Promise((resolve) => {
    const { overlay, dialog, footer } = frame(message, title);
    const cancelBtn = button(cancelLabel, 'quiet');
    const confirmBtn = button(confirmLabel, danger ? 'danger' : 'primary');
    footer.appendChild(cancelBtn);
    footer.appendChild(confirmBtn);
    dialog.appendChild(footer);
    document.body.appendChild(overlay);

    let resolved = false;
    const close = (result) => {
      if (resolved) return;
      resolved = true;
      if (overlay.parentNode) overlay.parentNode.removeChild(overlay);
      document.removeEventListener('keydown', onKey, true);
      resolve(result);
    };
    confirmBtn.onclick = () => close(true);
    cancelBtn.onclick = () => close(false);
    overlay.onmousedown = (e) => { if (e.target === overlay) close(false); };
    // A destructive confirm never fires on Enter and starts with Cancel
    // focused: a stray key must not delete anything.
    const onKey = (e) => {
      if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); close(false); }
      if (e.key === 'Enter' && !danger) { e.preventDefault(); e.stopPropagation(); close(true); }
    };
    document.addEventListener('keydown', onKey, true);
    requestAnimationFrame(() => (danger ? cancelBtn : confirmBtn).focus());
  });
}

/**
 * Themed multi-choice dialog. One button per option plus a Cancel.
 * Resolves with the chosen option's `value`, or null on cancel / overlay /
 * Escape.
 *
 * @param {string} message
 * @param {{
 *   title?: string,
 *   options: { label: string, value: any, color?: 'primary'|'secondary'|'danger' }[],
 *   cancelLabel?: string,
 * }} opts
 */
export function pidlyChoice(message, opts = {}) {
  const { options = [], cancelLabel = 'Cancel', title } = opts;
  return new Promise((resolve) => {
    const { overlay, dialog, footer } = frame(message, title);
    // Long labels read cleanly stacked; two short ones sit side by side.
    const stack = options.length > 2 || options.some(o => String(o.label).length > 22);
    if (stack) footer.classList.add('is-stack');
    dialog.appendChild(footer);
    document.body.appendChild(overlay);

    let resolved = false;
    const close = (result) => {
      if (resolved) return;
      resolved = true;
      if (overlay.parentNode) overlay.parentNode.removeChild(overlay);
      document.removeEventListener('keydown', onKey, true);
      resolve(result);
    };
    const cancelBtn = button(cancelLabel, 'quiet');
    cancelBtn.onclick = () => close(null);
    if (!stack) footer.appendChild(cancelBtn);
    for (const opt of options) {
      const variant = opt.color === 'danger' ? 'danger' : opt.color === 'secondary' ? 'quiet' : 'primary';
      const btn = button(opt.label, variant);
      btn.onclick = () => close(opt.value);
      footer.appendChild(btn);
    }
    if (stack) footer.appendChild(cancelBtn);
    overlay.onmousedown = (e) => { if (e.target === overlay) close(null); };
    const onKey = (e) => { if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); close(null); } };
    document.addEventListener('keydown', onKey, true);
    requestAnimationFrame(() => cancelBtn.focus());
  });
}

/**
 * Themed prompt dialog. Returns a Promise<string|null>: the trimmed input on
 * confirm, or null on cancel / overlay click / Escape.
 */
export function pidlyPrompt(message, opts = {}) {
  const {
    defaultValue = '',
    placeholder = '',
    confirmLabel = 'OK',
    cancelLabel = 'Cancel',
    confirmColor,
    title,
  } = opts;
  return new Promise((resolve) => {
    const { overlay, dialog, body, footer } = frame(message, title);
    const input = document.createElement('input');
    input.type = 'text';
    input.className = 'pdlg-input';
    input.value = defaultValue;
    input.placeholder = placeholder;
    body.after(input);
    const cancelBtn = button(cancelLabel, 'quiet');
    const confirmBtn = button(confirmLabel, isDanger(confirmColor) ? 'danger' : 'primary');
    footer.appendChild(cancelBtn);
    footer.appendChild(confirmBtn);
    dialog.appendChild(footer);
    document.body.appendChild(overlay);

    let resolved = false;
    const close = (result) => {
      if (resolved) return;
      resolved = true;
      if (overlay.parentNode) overlay.parentNode.removeChild(overlay);
      document.removeEventListener('keydown', onKey, true);
      resolve(result);
    };
    const submit = () => { const v = input.value.trim(); close(v.length > 0 ? v : null); };
    confirmBtn.onclick = submit;
    cancelBtn.onclick = () => close(null);
    overlay.onmousedown = (e) => { if (e.target === overlay) close(null); };
    const onKey = (e) => {
      if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); close(null); }
      if (e.key === 'Enter') { e.preventDefault(); e.stopPropagation(); submit(); }
    };
    document.addEventListener('keydown', onKey, true);
    requestAnimationFrame(() => { input.focus(); input.select(); });
  });
}

/** The dialog stylesheet, for React components that draw the same dialog. */
export function ensureDialogStyles() { injectCss(); }

/**
 * Override window.alert globally with the themed version.
 * Call this once at app startup (e.g. in main.jsx).
 */
export function installGlobalDialogs() {
  window.alert = pidlyAlert;
}
